import { useEffect, useRef, useState } from "react";

/**
 * Stable placement resolver: converts raw fit/space measurements into a stable placement
 * string with hysteresis to avoid rapid flipping.
 *
 * Policy:
 * 1) Prefer **right** when it fits.
 * 2) Otherwise choose **left** if it fits with a small margin (to avoid edge jitter).
 * 3) Otherwise fall back to vertical: **down**, then **up**.
 *
 * This hook is intentionally agnostic of DOM measurement. Callers should
 * obtain measurements (e.g., via `usePreferredSideFit` on both axes) and pass
 * them in via props.
 */
export type Placement = "right" | "left" | "down" | "up";

export const FLIP_HYSTERESIS_MS = 300; // minimum time between placement flips
export const FIT_MARGIN_PX = 6; // extra pixels required before switching horizontally

export interface UseStablePlacementArgs {
  open: boolean;
  fitsRight: boolean;
  hSpaces: { left: number; right: number } | null;
  hNeeded: number;
  fitsDown: boolean;
  vSpaces: { up: number; down: number } | null;
}

export function useStablePlacement({
  open,
  fitsRight,
  hSpaces,
  hNeeded,
  fitsDown,
  vSpaces,
}: UseStablePlacementArgs): Placement {
  const hLeft = hSpaces?.left ?? 0;
  const hRight = hSpaces?.right ?? 0;
  const vUp = vSpaces?.up ?? 0;
  const vDown = vSpaces?.down ?? 0;

  const [placement, setPlacement] = useState<Placement>("right");
  const lastFlipTsRef = useRef<number>(0);

  const decide = () => {
    // If measurements are not ready yet, bias to right to avoid initial flicker
    if (!hSpaces || !vSpaces) {
      return "right" as Placement;
    }

    if (fitsRight && hRight >= hNeeded) {
      return "right" as Placement;
    }
    if (hLeft >= hNeeded + FIT_MARGIN_PX) {
      return "left" as Placement;
    }
    if (fitsDown) {
      return "down" as Placement;
    }
    return (vUp >= hNeeded ? "up" : "down") as Placement;
  };

  // Decide once on open (after panel mounts and measurements run externally)
  useEffect(() => {
    if (!open) {
      return;
    }
    const next = decide();
    setPlacement((prev) => (prev !== next ? next : prev));
    lastFlipTsRef.current = Date.now();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Update cautiously if measurements change while open (resize/scroll/content)
  useEffect(() => {
    if (!open) {
      return;
    }
    const now = Date.now();
    const next = decide();
    const tooSoon = now - lastFlipTsRef.current < FLIP_HYSTERESIS_MS;

    const currentFits =
      placement === "right"
        ? hRight >= hNeeded
        : placement === "left"
          ? hLeft >= hNeeded
          : placement === "down"
            ? vDown >= hNeeded
            : vUp >= hNeeded;

    if (next !== placement && (!tooSoon || !currentFits)) {
      setPlacement(next);
      lastFlipTsRef.current = now;
    }
  }, [open, fitsRight, fitsDown, hLeft, hRight, vUp, vDown, hNeeded, placement]);

  return placement;
}
